import { ScaleTime } from 'd3-scale';
import { type PropsWithChildren, useCallback, useState } from 'react';

import { RelativeAbsolutePosition } from '../../../types/pointer-events.js';
import {
  SynchronizedSelectionContext,
  UpdateSelectionStateProps,
} from '../contexts/SynchronizedSelectionContext.js';

const getSelectionValue = (
  position: RelativeAbsolutePosition,
  scale: ScaleTime<number, number>,
): number => {
  const value = position.relative.x;

  return scale.invert(value).valueOf();
};

const getSelectionValues = (
  selection: [RelativeAbsolutePosition, RelativeAbsolutePosition],
  scale: ScaleTime<number, number>,
): [number, number] => {
  const [start, end] = selection;

  return [getSelectionValue(start, scale), getSelectionValue(end, scale)];
};

type SelectionActive = {
  selectedChartId: string;
  startX: number;
  endX: number;
};
type SelectionInactive = {
  selectedChartId: undefined;
  startX: undefined;
  endX: undefined;
};
type State = {
  disabled: boolean;
} & (SelectionActive | SelectionInactive);

const initialState = {
  disabled: false,
  selectedChartId: undefined,
  startX: undefined,
  endX: undefined,
};

export const SynchronizedSelectionProvider = ({
  children,
}: PropsWithChildren) => {
  const [state, setState] = useState<State>(initialState);

  const { selectedChartId, startX, endX, disabled } = state;

  const resetSelectionState = () => {
    setState(initialState);
  };

  const updateSelectionState = useCallback(
    (props?: UpdateSelectionStateProps) => {
      if (!props) {
        resetSelectionState();
      } else {
        const { selection, chartId, scale } = props;

        const [startX, endX] = getSelectionValues(selection, scale);

        setState((prevState) => {
          return {
            ...prevState,
            startX,
            endX,
            selectedChartId: chartId,
          };
        });
      }
    },
    [],
  );

  return (
    <SynchronizedSelectionContext.Provider
      value={{
        selectedChartId,
        startX,
        endX,
        disabled,
        updateSelectionState,
        resetSelectionState,
      }}
    >
      {children}
    </SynchronizedSelectionContext.Provider>
  );
};
